import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AccountsPayableService } from './accounts-payable.service';
import { TrafficFinesService } from './traffic-fines.service';

export interface TrafficFinePaymentSyncOptions {
  /** Quando false, apenas reflete o status da conta a pagar na UI (sem PUT). */
  persist?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TrafficFineAccountsPayableSyncService {
  private static readonly TARGET_PAYMENT_STATUS = 'Pago';

  /** Evita PUT repetido da mesma multa na mesma sessão do navegador. */
  private readonly persistedSyncFineIds = new Set<number>();
  private readonly persistedSyncStoragePrefix = 'trafficFinePaymentSync:';

  private hasPersistedSync(fineId: number): boolean {
    if (this.persistedSyncFineIds.has(fineId)) {
      return true;
    }
    try {
      return sessionStorage.getItem(`${this.persistedSyncStoragePrefix}${fineId}`) === '1';
    } catch {
      return false;
    }
  }

  private markPersistedSync(fineId: number): void {
    this.persistedSyncFineIds.add(fineId);
    try {
      sessionStorage.setItem(`${this.persistedSyncStoragePrefix}${fineId}`, '1');
    } catch {
      // ignore quota / privacy mode
    }
  }

  constructor(
    private accountsPayableService: AccountsPayableService,
    private trafficFinesService: TrafficFinesService
  ) {}

  syncPaymentStatus(
    fine: any,
    userName?: string,
    options: TrafficFinePaymentSyncOptions = {}
  ): Observable<any> {
    const persist = options.persist !== false;
    const accountsPayableId = this.resolveAccountsPayableId(fine);
    if (!accountsPayableId) {
      return of(fine);
    }

    return this.accountsPayableService.getAccountsPayableById(accountsPayableId).pipe(
      switchMap(ap => {
        const targetStatus = this.resolveTargetPaymentStatus(ap?.status ?? '');
        if (!targetStatus) {
          return of(fine);
        }
        if (this.isTrafficFineAlreadyPaid(this.resolvePaymentStatus(fine))) {
          return of(fine);
        }

        if (!persist) {
          return of(this.applyResolvedPaymentStatus(fine, targetStatus));
        }

        const fineId = this.resolveFineId(fine);
        if (fineId != null && this.hasPersistedSync(fineId)) {
          return of(this.applyResolvedPaymentStatus(fine, targetStatus));
        }

        return this.updateTrafficFinePaymentStatus(fine, userName, targetStatus);
      }),
      catchError(err => {
        console.warn('[TrafficFineAccountsPayableSync] Erro ao sincronizar paymentStatus:', err);
        return of(fine);
      })
    );
  }

  syncPaymentStatusBatch(
    fines: any[],
    userName?: string,
    options: TrafficFinePaymentSyncOptions = {}
  ): Observable<any[]> {
    if (!fines?.length) {
      return of([]);
    }

    const candidates = fines.filter(fine => this.isSyncCandidate(fine));
    if (!candidates.length) {
      return of(fines);
    }

    const syncOps = candidates.map(fine =>
      this.syncPaymentStatus(fine, userName, options).pipe(catchError(() => of(fine)))
    );

    return forkJoin(syncOps).pipe(
      map(updatedCandidates => {
        const updatedById = new Map<number, any>();
        updatedCandidates.forEach(updated => {
          const id = this.resolveFineId(updated);
          if (id != null) {
            updatedById.set(id, updated);
          }
        });
        return fines.map(row => {
          const id = this.resolveFineId(row);
          return id != null && updatedById.has(id) ? updatedById.get(id)! : row;
        });
      }),
      catchError(() => of(fines))
    );
  }

  isSyncCandidate(fine: any): boolean {
    if (!this.resolveAccountsPayableId(fine)) {
      return false;
    }
    return !this.isTrafficFineAlreadyPaid(this.resolvePaymentStatus(fine));
  }

  private updateTrafficFinePaymentStatus(
    fine: any,
    userName: string | undefined,
    targetStatus: string
  ): Observable<any> {
    const fineId = this.resolveFineId(fine);
    if (!fineId) {
      return of(fine);
    }

    const payload = {
      PaymentStatus: targetStatus,
      UserName: userName?.trim() || 'Sistema'
    };

    return this.trafficFinesService.update(fineId, payload).pipe(
      map(updated => {
        this.markPersistedSync(fineId);
        return this.applyResolvedPaymentStatus(
          {
            ...fine,
            ...(updated && typeof updated === 'object' ? updated : {})
          },
          targetStatus
        );
      }),
      catchError(err => {
        console.warn('[TrafficFineAccountsPayableSync] Erro ao atualizar paymentStatus da multa:', err);
        return of(fine);
      })
    );
  }

  private resolveTargetPaymentStatus(accountsPayableStatus: string): string | null {
    if (this.isAccountsPayablePending(accountsPayableStatus)) {
      return null;
    }
    if (this.isAccountsPayablePaid(accountsPayableStatus)) {
      return TrafficFineAccountsPayableSyncService.TARGET_PAYMENT_STATUS;
    }
    return null;
  }

  private applyResolvedPaymentStatus(fine: any, targetStatus: string): any {
    return {
      ...fine,
      paymentStatus: targetStatus,
      PaymentStatus: targetStatus
    };
  }

  private resolveAccountsPayableId(fine: any): number | null {
    const raw = fine?.accountsPayableId ?? fine?.AccountsPayableId;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private resolveFineId(fine: any): number | null {
    const raw = fine?.id ?? fine?.Id;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private resolvePaymentStatus(fine: any): string {
    return String(fine?.paymentStatus ?? fine?.PaymentStatus ?? '').trim();
  }

  private normalizeStatus(status: string): string {
    return String(status ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  isAccountsPayablePending(status: string): boolean {
    const normalized = this.normalizeStatus(status);
    return normalized === 'pendente';
  }

  isAccountsPayablePaid(status: string): boolean {
    const normalized = this.normalizeStatus(status);
    return normalized === 'pago' || normalized.includes('parcialmente');
  }

  isTrafficFineAlreadyPaid(paymentStatus: string): boolean {
    const normalized = this.normalizeStatus(paymentStatus).replace(/[_\s-]+/g, '');
    return normalized === 'pago' || normalized.includes('pagopela') || normalized.includes('pagopelo');
  }
}
